import { useCallback, useEffect, useState } from 'react';
import { Heart, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCollections } from '@/hooks/useCollections';
import { useBoardMembership } from '@/hooks/useBoardMembership';
import { BoardTree } from './BoardTree';
import { CreateBoardInline } from './CreateBoardInline';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import GradientHeart from '../GradientHeart';
import { logger } from '@/lib/logger';
// Removed framer-motion for memory optimization - using CSS transitions instead

interface BookmarkMenuProps {
  adId: string;
  trigger: React.ReactNode;
}

export function BookmarkMenu({ adId, trigger }: BookmarkMenuProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [favoritesId, setFavoritesId] = useState<string | null>(null);
  const [favBusy, setFavBusy] = useState(false);

  // NEW: controls inline "create board" UI triggered from header +
  const [showInlineCreate, setShowInlineCreate] = useState(false);

  // Load collections only when menu is open
  const { collectionsTree, loading: collectionsLoading, createCollection, refetch } =
    useCollections({ enabled: open, realtime: open });

  // Membership scoped to the open menu
  const {
    isBoardChecked,
    toggleBoard,
    commitChanges,
    hasPendingChanges,
    savedBoardsCount,
  } = useBoardMembership(open ? adId : null);

  // Helper: get or create Favorites id (RPC first; fallback to direct select/insert)
  const getFavoritesId = useCallback(async (): Promise<string> => {
    // try RPC
    try {
      const { data, error } = await (supabase as any).rpc('get_or_create_favorites_id');
      if (error) throw error;
      if (data) return String(data);
    } catch (e) {
      logger.warn('RPC get_or_create_favorites_id failed; falling back', {
        error: e,
      });
    }

    // fallback: select existing
    try {
      const { data, error } = await supabase
        .from('user_collections' as any)
        .select('id')
        .eq('is_system', true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      if (data && 'id' in data) return data.id as string;
    } catch { /* noop */ }

    // fallback: create one manually
    const { data: inserted, error: insErr } = await supabase
      .from('user_collections' as any)
      .insert({ name: 'Favorites', is_system: true })
      .select('id')
      .single();
    if (insErr) throw insErr;
    if (inserted && 'id' in inserted) return String(inserted.id);
    throw new Error('Failed to create Favorites collection');
  }, []);

  // Ensure Favorites exists and cache id when menu opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const id = await getFavoritesId();
        setFavoritesId(id);
        await refetch();
      } catch (err) {
        logger.error('Failed to ensure Favorites', err as Error, {
          adId,
        });
      }
    })();
  }, [open, getFavoritesId, refetch]);

  const handleConfirm = async () => {
    await commitChanges();
    setOpen(false);
    setSearchQuery('');
    setShowInlineCreate(false);
  };

  const handleCreateBoard = async (name: string, parentId: string | null = null) => {
    const created = await createCollection(name, parentId);
    if (created?.id) toggleBoard(created.id, true); // force add
  };

  const handleCreateSubBoard = async (parentId: string, name: string) => {
    await handleCreateBoard(name, parentId);
  };

  // Hide system Favorites in the tree (we show pinned row)
  const rawTree = searchQuery
    ? collectionsTree.filter((b) => b.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : collectionsTree;
  const filteredTree = rawTree.filter((b) => !(b.is_system && b.slug === 'favorites'));

  // Click for favorites (force next state)
  const onToggleFavorites = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (favBusy) return;
    setFavBusy(true);
    try {
      const id = favoritesId ?? (await getFavoritesId());
      setFavoritesId(id);

      const next = !isBoardChecked(id);
      toggleBoard(id, next);              // force local state so heart updates immediately
      // (persist will happen on Confirm)
    } catch (err) {
      logger.error('Toggle favorites failed', err as Error, {
        adId,
        favoritesId: id,
      });
    } finally {
      setFavBusy(false);
    }
  }, [favBusy, favoritesId, getFavoritesId, isBoardChecked, toggleBoard]);

 const content = (
    <div className="flex flex-col h-full text-white">
      {/* Header */}
      <div
        className={cn(
          "px-4 py-3 border-b border-white/10",
          "bg-white/5"
        )}
      >
        <div className="flex items-center justify-between mb-3">
          <h3
            key={savedBoardsCount}
            className="text-sm font-semibold text-white animate-[slideInDown_0.2s_ease-out]"
          >
            {savedBoardsCount > 0 ? "Ad saved ✓" : "Add to board"}
          </h3>

          {/* RIGHT ACTIONS: + create + close */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs text-white/70 hover:text-white hover:bg-white/10"
              onClick={() => {
                setOpen(false);
                setSearchQuery("");
                setShowInlineCreate(false);
              }}
            >
              {hasPendingChanges ? "Cancel" : "Close"}
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search boards..."
            className="pl-9 pr-9 h-9 bg-white/10 border-white/10 text-white placeholder:text-white/40 focus:border-white/20"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Pinned Favorites */}
      <div
        className={cn(
          "px-4 py-2 border-b border-white/10",
          "bg-white/5"
        )}
      >
        <button
          onClick={onToggleFavorites}
          disabled={!favoritesId && favBusy}
          className={cn(
            "w-full flex items-center gap-3 px-3 py-2 rounded-md transition-colors",
            "hover:bg-white/10",
            "bg-white/5",
            "border border-white/10",
            favBusy && "opacity-70"
          )}
        >
          <div className="ml-1 flex-shrink-0">
            {favoritesId && isBoardChecked(favoritesId) ? (
              <GradientHeart className="h-5 w-5 transition-all duration-200" />
            ) : (
              <Heart className="h-5 w-5 text-white/50 transition-all duration-200" />
            )}
          </div>
          <span className="text-sm font-medium select-none text-white/90">Favorites</span>
        </button>
      </div>

      {/* Board List */}
      <ScrollArea
        className={cn(
          "flex-1 px-2 py-2",
          "bg-transparent"
        )}
      >

        {/* Inline create at the TOP when user hits + */}
        {showInlineCreate && open && !collectionsLoading && !searchQuery && (
          <div className="mb-2 px-1">
            <CreateBoardInline
              onCreate={async (name) => {
                await handleCreateBoard(name, null);
                setShowInlineCreate(false);
              }}
            />
          </div>
        )}

        {!open ? (
          <div className="py-8 text-center text-sm text-white/50" />
        ) : collectionsLoading ? (
          <div className="py-8 text-center text-sm text-white/50">
            Loading boards...
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="py-8 text-center text-sm text-white/50">
            {searchQuery ? "No boards found" : "No boards yet"}
          </div>
        ) : (
          <div className="space-y-1 px-1">
            {filteredTree.map((board) => (
              <BoardTree
                key={board.id}
                board={board}
                isChecked={isBoardChecked}
                onToggle={(id, next) => toggleBoard(id, next)}
                onCreateSub={handleCreateSubBoard}
                searchQuery={searchQuery}
              />
            ))}
          </div>
        )}

        {!searchQuery && open && !showInlineCreate && (
          <div className="mt-2 px-1">
            <CreateBoardInline onCreate={(name) => handleCreateBoard(name, null)} />
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      {hasPendingChanges && (
        <div
          className={cn(
            "border-t border-white/10 p-3",
            "bg-white/5",
            "backdrop-blur-md",
            "animate-[slideInUp_0.2s_ease-out]"
          )}
        >
          <Button onClick={handleConfirm} className="w-full" size="sm">
            Confirm
          </Button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) {
            setSearchQuery("");
            setShowInlineCreate(false);
          }
        }}
      >
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          className={cn(
            "h-[85vh] p-0 rounded-t-2xl border-t border-white/10",
            // Dark glossy glass effect
            "bg-[#13171c]/95",
            "backdrop-blur-xl",
            "shadow-2xl"
          )}
        >
          {content}
        </SheetContent>

      </Sheet>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setSearchQuery("");
          setShowInlineCreate(false);
        }
      }}
      modal={true}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-80 p-0 rounded-xl border border-white/10 shadow-2xl",
          // Dark glossy glass card
          "bg-[#13171c]/95",
          "backdrop-blur-xl"
        )}
        align="end"
        sideOffset={5}
        onInteractOutside={(e) => {
          // Prevent closing when clicking inside the dropdown
          const target = e.target as HTMLElement;
          if (target?.closest('[role="menu"]')) {
            e.preventDefault();
          }
        }}
      >
        <div className="h-[500px]">{content}</div>
      </PopoverContent>

    </Popover>
  );
}

